import {gql} from '@apollo/client';
import {createStyles, Text} from '@mantine/core';
import React, {useCallback, useEffect, useMemo} from 'react';

import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';

import RoutineForm from './RoutineForm';
import {sanitizeTriggerConfig} from './routineFormValidation';
import {
  buildMessageBlocks,
  EventTriggerConfig,
  extractFileBlocks,
  extractPromptFromBlocks,
  FileMessageBlock,
  GqlMessageBlock,
  gqlBlocksToMessageBlocks,
  RoutineStatus,
  TimeTriggerConfig,
  TriggerType,
} from './RoutineTypes';
import useAllowedEventTriggers from './useAllowedEventTriggers';
import useRoutineFileUpload from './useRoutineFileUpload';
import useRoutineForm, {
  getDefaultEventTriggerConfig,
  getDefaultTimeTriggerConfig,
  RoutineFormState,
} from './useRoutineForm';
import useRoutineMutations from './useRoutineMutations';

const ROUTINE_QUERY = gql`
  query RoutineQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    routine(id: $id) {
      id
      name
      description
      messageBlocks {
        ... on DisplayTextBlockNode {
          text
          displayType
        }
        ... on DisplayFileBlockNode {
          externalFileId
          filename
          mediaType
        }
      }
      agentType
      triggerType
      triggerConfig
      status
      createdAt
      modifiedAt
      creatorId
    }
  }
`;

interface RoutineQueryData {
  routine: {
    id: string;
    name: string;
    description: string | null;
    messageBlocks: Array<GqlMessageBlock>;
    agentType: string;
    triggerType: TriggerType;
    triggerConfig: Record<string, unknown>;
    status: RoutineStatus;
    createdAt: string;
    modifiedAt: string;
    creatorId: string | null;
  } | null;
}

const DEFAULT_AGENT_TYPE = 'ASSISTANT';

export type DetailPanelMode =
  | {type: 'edit'; routineId: string}
  | {type: 'create'; example?: RoutineFormState; exampleId?: string};

interface RoutineDetailPanelProps {
  mode: DetailPanelMode;
  onClose: () => void;
  onMutationSuccess: () => Promise<void>;
  timeBasedEnabled: boolean;
  eventBasedEnabled: boolean;
}

const useStyles = createStyles((theme) => ({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    padding: theme.spacing.md,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
}));

function getDefaultCreateState(timeBasedEnabled: boolean, eventBasedEnabled: boolean): RoutineFormState {
  if (timeBasedEnabled && !eventBasedEnabled) {
    return {
      name: '',
      description: '',
      prompt: '',
      fileBlocks: [],
      triggerType: TriggerType.TIME_BASED,
      triggerConfig: getDefaultTimeTriggerConfig(),
    };
  }
  if (eventBasedEnabled && !timeBasedEnabled) {
    return {
      name: '',
      description: '',
      prompt: '',
      fileBlocks: [],
      triggerType: TriggerType.EVENT_BASED,
      triggerConfig: getDefaultEventTriggerConfig(),
    };
  }
  return {name: '', description: '', prompt: '', fileBlocks: [], triggerType: null, triggerConfig: {}};
}

function buildFormStateFromQuery(data: RoutineQueryData['routine']): RoutineFormState | null {
  if (data == null) {
    return null;
  }
  const messageBlocks = gqlBlocksToMessageBlocks(data.messageBlocks);
  return {
    name: data.name ?? '',
    description: data.description ?? '',
    prompt: extractPromptFromBlocks(messageBlocks),
    fileBlocks: extractFileBlocks(messageBlocks),
    triggerType: data.triggerType,
    triggerConfig: data.triggerConfig as Partial<TimeTriggerConfig> | Partial<EventTriggerConfig>,
  };
}

export default function RoutineDetailPanel({
  mode,
  onClose,
  onMutationSuccess,
  timeBasedEnabled,
  eventBasedEnabled,
}: RoutineDetailPanelProps): JSX.Element {
  const {classes} = useStyles();
  const isEdit = mode.type === 'edit';
  const routineId = isEdit ? mode.routineId : null;

  const queryResult = useSkippableBenchlingQuery<RoutineQueryData>(ROUTINE_QUERY, {
    skip: !isEdit,
    variables: {id: routineId},
    fetchPolicy: 'network-only',
  });

  const routineData = queryResult.type === QueryResultType.SUCCESS ? queryResult.data.routine : null;

  const defaultCreateState = useMemo(
    () => getDefaultCreateState(timeBasedEnabled, eventBasedEnabled),
    [timeBasedEnabled, eventBasedEnabled]
  );

  const initialFormState = useMemo(() => {
    if (!isEdit) {
      return mode.type === 'create' && mode.example != null ? mode.example : defaultCreateState;
    }
    return buildFormStateFromQuery(routineData) ?? defaultCreateState;
  }, [isEdit, mode, routineData, defaultCreateState]);

  const {formState, dispatch, isDirty, reset} = useRoutineForm(initialFormState);

  // Reset form when the routine data loads or changes
  useEffect(() => {
    if (isEdit && routineData != null) {
      const newState = buildFormStateFromQuery(routineData);
      if (newState != null) {
        reset(newState);
      }
    }
  }, [isEdit, routineData, reset]);

  const {triggers: allowedEventTriggers} = useAllowedEventTriggers({enabled: eventBasedEnabled});

  const mutations = useRoutineMutations(onMutationSuccess);

  const handleNameChange = useCallback(
    (value: string) => {
      dispatch({type: 'SET_NAME', value});
    },
    [dispatch]
  );
  const handleDescriptionChange = useCallback(
    (value: string) => {
      dispatch({type: 'SET_DESCRIPTION', value});
    },
    [dispatch]
  );
  const handlePromptChange = useCallback(
    (value: string) => {
      dispatch({type: 'SET_PROMPT', value});
    },
    [dispatch]
  );
  const handleRemoveFileBlock = useCallback(
    (index: number) => {
      dispatch({type: 'REMOVE_FILE_BLOCK', index});
    },
    [dispatch]
  );
  const handleFileUploaded = useCallback(
    (fileBlock: FileMessageBlock) => {
      dispatch({type: 'ADD_FILE_BLOCK', fileBlock});
    },
    [dispatch]
  );
  const {uploadFile, isUploading} = useRoutineFileUpload(handleFileUploaded);
  const handleTriggerTypeChange = useCallback(
    (value: TriggerType) => {
      dispatch({type: 'SET_TRIGGER_TYPE', value});
    },
    [dispatch]
  );
  const handleTriggerConfigChange = useCallback(
    (updates: Partial<TimeTriggerConfig | EventTriggerConfig>) => {
      dispatch({type: 'UPDATE_TRIGGER_CONFIG', updates});
    },
    [dispatch]
  );

  const handleSave = useCallback(async () => {
    if (formState.triggerType == null) {
      return;
    }
    const messageBlocks = buildMessageBlocks(formState.prompt, formState.fileBlocks);
    const triggerConfig = sanitizeTriggerConfig(formState.triggerType, formState.triggerConfig);
    if (isEdit && routineId != null) {
      const success = await mutations.updateRoutine(routineId, {
        name: formState.name.trim(),
        description: formState.description,
        messageBlocks,
        triggerConfig,
      });
      if (success) {
        if (queryResult.type === QueryResultType.SUCCESS) {
          await queryResult.refetch();
        }
      }
    } else {
      const newId = await mutations.createRoutine({
        name: formState.name.trim(),
        description: formState.description || undefined,
        messageBlocks,
        agentType: DEFAULT_AGENT_TYPE,
        triggerType: formState.triggerType,
        triggerConfig,
      });
      if (newId != null) {
        onClose();
      }
    }
  }, [isEdit, routineId, formState, mutations, queryResult, onClose]);

  const handlePause = useCallback(async () => {
    if (routineId != null) {
      await mutations.pauseRoutine(routineId);
      if (queryResult.type === QueryResultType.SUCCESS) {
        await queryResult.refetch();
      }
    }
  }, [routineId, mutations, queryResult]);

  const handleResume = useCallback(async () => {
    if (routineId != null) {
      await mutations.resumeRoutine(routineId);
      if (queryResult.type === QueryResultType.SUCCESS) {
        await queryResult.refetch();
      }
    }
  }, [routineId, mutations, queryResult]);

  const handleArchive = useCallback(async () => {
    if (routineId != null) {
      const success = await mutations.archiveRoutine(routineId, 'Retired');
      if (success) {
        onClose();
      }
    }
  }, [routineId, mutations, onClose]);

  const isLoading = isEdit && queryResult.type === QueryResultType.PENDING;

  if (isLoading) {
    return (
      <div className={classes.loading}>
        <Text size="sm" color="dimmed">
          Loading...
        </Text>
      </div>
    );
  }

  return (
    <div className={classes.panel}>
      <RoutineForm
        isEdit={isEdit}
        formState={formState}
        onNameChange={handleNameChange}
        onDescriptionChange={handleDescriptionChange}
        onPromptChange={handlePromptChange}
        onRemoveFileBlock={handleRemoveFileBlock}
        onAddFile={uploadFile}
        isUploading={isUploading}
        onTriggerTypeChange={handleTriggerTypeChange}
        onTriggerConfigChange={handleTriggerConfigChange}
        isDirty={isDirty}
        isSaving={mutations.isSaving}
        error={mutations.error}
        onSave={handleSave}
        onCancel={onClose}
        onPause={handlePause}
        onResume={handleResume}
        onArchive={handleArchive}
        currentStatus={routineData?.status}
        allowedEventTriggers={allowedEventTriggers}
        timeBasedEnabled={timeBasedEnabled}
        eventBasedEnabled={eventBasedEnabled}
      />
    </div>
  );
}
